import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { nanoid } from 'nanoid';

const useStore = create(
  subscribeWithSelector((set, get) => ({
    // List state
    listId: null,
    content: '',
    isProtected: false,
    isOwner: false,
    // Add these to the state
    tier: 'free', // 'free' or 'paid'
    hasPassword: false,
    createdAt: null,
    expiresAt: null,
    lastSaved: null,
    isSaving: false,
    isLoading: true,
    error: null,
    
    // UI state
    showPasswordDialog: false,
    showLockDialog: false,
    showShareDialog: false,
    showPasswordSetDialog: false, // Add this line
    password: null,
    email: null,
    
    // WebSocket state
    ws: null,
    isConnected: false,
    collaborators: 0,
    
    // Actions
    initializeList: (listId = null) => {
      const id = listId || nanoid(8);
      set({ 
        listId: id, 
        isLoading: true,
        error: null 
      });
      
      // Update URL without reload
      if (!listId) {
        window.history.pushState({}, '', `/${id}`);
      }
      
      return id;
    },

    // Add these actions
    openPasswordSetDialog: () => set({ showPasswordSetDialog: true }),
    closePasswordSetDialog: () => set({ showPasswordSetDialog: false }),
    
    setContent: (content) => set({ content }),
    
    setListData: (data) => set({
      content: data.content || '',
      tier: data.tier || 'free',
      hasPassword: data.hasPassword || false,
      isProtected: data.isProtected || false,
      isOwner: data.isOwner || false,
      expiresAt: data.expiresAt || null,
      createdAt: data.created || null,
      lastSaved: data.lastModified || null,
      isLoading: false,
    }),
    
    setSaving: (isSaving) => set({ isSaving }),
    
    setError: (error) => set({ error, isLoading: false }),
    
    // Dialog controls
    openPasswordDialog: () => set({ showPasswordDialog: true }),
    closePasswordDialog: () => set({ showPasswordDialog: false, password: null }),
    
    openLockDialog: () => set({ showLockDialog: true }),
    closeLockDialog: () => set({ showLockDialog: false, email: null }),
    
    openShareDialog: () => set({ showShareDialog: true }),
    closeShareDialog: () => set({ showShareDialog: false }),
    
    setPassword: (password) => set({ password }),
    setEmail: (email) => set({ email }),
    
    // WebSocket actions
    setWebSocket: (ws) => set({ ws }),
    setConnected: (isConnected) => set({ isConnected }),
    setCollaborators: (count) => set({ collaborators: count }),
    
    // Lock list
    lockList: async (email) => {
      const { listId } = get();
      set({ email });
      
      try {
        const { listAPI } = await import('@api/client');
        const data = await listAPI.lockList(listId, email);
        
        if (data.checkoutUrl) {
          window.location.href = data.checkoutUrl;
        }
      } catch (error) {
        set({ error: 'Failed to lock list' });
        throw error;
      }
    },
    
// Unlock for editing
unlockList: async (password) => {
  const { listId } = get();
  
  try {
    const { listAPI } = await import('@api/client');
    const result = await listAPI.verifyPassword(listId, password);
    
    if (result.valid) {
      set({ 
        password,
        isOwner: true,
        showPasswordDialog: false 
      });
      
      // Store in session
      sessionStorage.setItem(`password_${listId}`, password);
      
      return true;
    } else {
      throw new Error('Invalid password');
    }
  } catch (error) {
    set({ error: 'Invalid password' });
    return false;
  }
},
    
    // Clean up
    reset: () => {
      const { ws } = get();
      if (ws) {
        ws.close();
      }
      
      set({
        listId: null,
        content: '',
        isProtected: false,
        isOwner: false,
        // Add these to the state
        tier: 'free', // 'free' or 'paid'
        hasPassword: false,
        createdAt: null,
        expiresAt: null,
        lastSaved: null,
        isSaving: false,
        isLoading: true,
        error: null,
        showPasswordDialog: false,
        showLockDialog: false,
        showShareDialog: false,
        showPasswordSetDialog: false, // Add this line
        password: null,
        email: null,
        ws: null,
        isConnected: false,
        collaborators: 0,
      });
    },
  }))
);

export default useStore;